import { useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import Carousel from '@comp/Carousel/Carousel';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import FreeCourseCard from '@comp/FreeCourseCard/FreeCourseCard';
import Dialog from '@comp/Dialog/Dialog';
import useEnroll from '@hooks/useEnroll';
import { segment } from '@actions/analytics';
import {
  SYLLABUS_SEE_ALL_CLICKED,
  GTP_SEE_ALL_CLICKED
} from '@constants/segment';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';

// PlusHome, FreeCourse, SpecialClasses

const ClassFeedBlock = ({
  classFeedBlock,
  viewAllLink,
  viewAllHref,
  type,
  topic,
  dataAnalytics,
  lastPrimarySource,
  goalUID
}) => {
  // const count = classFeedBlock?.extraBlockinfo?.count;
  // const feedBlockType = classFeedBlock.type;
  // const seeAllLink =
  //   count > 3 ? `/goal/${goalSlug}/${goalUID}/${feedBlockType}` : null;
  // const href = '/goal/[goalSlug]/[goalUID]/[type]';
  const viewRef = useRef();
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const { label, type: feedblockType, description } = classFeedBlock;
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const showAsLesson = isRecordedGoal && feedblockType === 'live_now';

  const message =
    'This course will be removed from your schedule and you will stop receiving updates related to this course';
  const goalSegmentData = useGoalEventProps({ goalUID });

  const { isUnEnrollModalOpen, toggleEnroll, acceptOnClick, onCloseModal } =
    useEnroll({
      dataAnalytics,
      lastPrimarySource,
      lastPrimarySourceSection: label,
      isRecordedGoal
    });

  if (!classFeedBlock.courses?.length) {
    return null;
  }

  const RenderFreeCourseCards = () =>
    classFeedBlock.courses.map((courseData, _index) => (
      <FreeCourseCard
        label={label}
        index={_index}
        lastPrimarySource={lastPrimarySource}
        key={courseData.uid}
        course={courseData}
        dataAnalytics={dataAnalytics}
      />
    ));

  const RenderPlusCourseCards = () =>
    classFeedBlock.courses.map((courseData, _index) => (
      <PlusCourseCard
        label={label}
        index={_index}
        lastPrimarySource={lastPrimarySource}
        key={courseData.uid}
        course={{ ...courseData, showAsLesson }}
        type={type}
        topic={topic}
        toggleEnroll={toggleEnroll(courseData)}
        dataAnalytics={dataAnalytics}
      />
    ));

  const seeAllClickEvent = () => {
    if (isGTP) {
      segment.track(GTP_SEE_ALL_CLICKED, {
        ...goalSegmentData,
        last_primary_source: lastPrimarySource,
        last_primary_source_section: 'special classes see all'
      });
    } else {
      const goal = classFeedBlock.courses?.[0]?.goal;

      if (goal) {
        segment.track(SYLLABUS_SEE_ALL_CLICKED, {
          expansion_type: classFeedBlock.label,
          goal_name: goal.name,
          goal_uid: goal.uid,
          is_subscription_active: hasActiveSubscriptionToGoal(goal, goal.uid),
          subscription_duration: 'NA',
          subscription_end_date: 'NA',
          last_primary_source: lastPrimarySource,
          last_primary_source_section: classFeedBlock.label
        });
      }
    }
  };

  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={message}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Carousel
        label={label}
        description={description}
        seeAllLink={viewAllLink}
        href={viewAllHref}
        dataAnalytics="goal-course"
        ref={viewRef}
        handleClickSeeAll={seeAllClickEvent}
        $css={css`
          margin-bottom: 56px;
        `}
      >
        {type === 'freeCourse'
          ? RenderFreeCourseCards()
          : RenderPlusCourseCards()}
      </Carousel>
    </>
  );
};

ClassFeedBlock.propTypes = {
  viewAllLink: PropTypes.string.isRequired,
  viewAllHref: PropTypes.string.isRequired,
  classFeedBlock: PropTypes.objectOf(PropTypes.unknown).isRequired,
  type: PropTypes.string.isRequired,
  topic: PropTypes.objectOf(PropTypes.unknown),
  dataAnalytics: PropTypes.string,
  lastPrimarySource: PropTypes.string
};

ClassFeedBlock.defaultProps = {
  topic: undefined,
  dataAnalytics: '',
  lastPrimarySource: ''
};

export default ClassFeedBlock;
